import type React from 'react'
import { Link, useParams } from 'react-router-dom'
import { api, type EventSummary, type Winner } from '../../api/public'
import { withToPar } from '../../components/toPar'
import { STATE_LABEL } from '../../components/eventState'
import { Empty, Failed, Loading } from '../../components/Async'
import { useApi } from '../../components/useApi'
import { useSeason } from './useSeason'
import { SeasonSwitcher } from './SeasonSwitcher'
import { usePlayerPaths } from '../../components/playerPaths'

/** The season's events, each showing its state (FR-059). */
export function Schedule() {
  const { seasonId } = useParams()
  const season = useSeason(seasonId)
  const schedule = useApi(
    (s) =>
      season.state === 'ready' ? api.schedule(season.data.id, s) : new Promise<never>(() => {}),
    [season.state === 'ready' ? season.data.id : 0],
  )

  if (season.state === 'loading') return <Loading what="the season" />
  if (season.state === 'error') return <Failed what="The season" error={season.error} />
  if (schedule.state === 'loading') return <Loading what="the schedule" />
  if (schedule.state === 'error') return <Failed what="The schedule" error={schedule.error} />

  return (
    <section>
      <h2>{season.data.name} schedule</h2>
      <SeasonSwitcher current={season.data.id} view="schedule" />
      {schedule.data.length === 0 ? (
        <Empty>No weeks have been scheduled yet.</Empty>
      ) : (
        <ScheduleTable events={schedule.data} />
      )}
    </section>
  )
}

/**
 * The season as a table: one row per week, as Fringe Golfers laid it out.
 *
 * # One row, not a card
 *
 * It was a card per week -- a title line, a meta line and a winner line -- so a
 * fifteen-week season was forty-five lines on a wide screen with most of the
 * width empty. Each fact now has a column, so the eye runs down a column to
 * compare weeks: which were majors, who won, what it took.
 *
 * A major carries a star beside its date, the convention the league's previous
 * software used. On a phone the table scrolls sideways inside its own container
 * rather than pushing the page (FR-035i).
 */
function ScheduleTable({ events }: { events: EventSummary[] }) {
  return (
    <div className="scroll-x">
      <table className="table schedule-table table-cards" data-testid="schedule-table">
        <thead>
          <tr>
            <th scope="col" className="numeric">
              Wk
            </th>
            <th scope="col">Date</th>
            <th scope="col">Course</th>
            <th scope="col">Tee</th>
            <th scope="col" className="numeric">
              Holes
            </th>
            <th scope="col" className="numeric">
              Par
            </th>
            <th scope="col">Result</th>
            <th scope="col">Status</th>
          </tr>
        </thead>
        <tbody>
          {events.map((event) => (
            <tr
              key={event.id}
              className={event.event_type.is_major ? 'schedule-major' : undefined}
              data-testid={`schedule-row-${event.id}`}
            >
              <td className="numeric">{event.week_number}</td>
              <td>
                <time dateTime={event.scheduled_date}>{event.scheduled_date}</time>
                {event.event_type.is_major && (
                  <abbr className="major-star" title={event.event_type.display_name}>
                    {' '}
                    ★
                  </abbr>
                )}
              </td>
              <th scope="row">
                <Link to={`/events/${event.id}`}>{event.course}</Link>
              </th>
              <td>{event.tee}</td>
              <td className="numeric">{event.holes_expected}</td>
              <td className="numeric">{event.par || '—'}</td>
              <WinnerCells winners={event.winners} />
              <td>
                <span className={`badge badge-${event.state}`}>{STATE_LABEL[event.state]}</span>
                {event.state === 'scheduled' && (
                  <Link to={`/events/${event.id}/stroke-sheet`} className="schedule-link">
                    Stroke sheet
                  </Link>
                )}
                {/* A PROVISIONAL week is still being played (FR-065) -- somebody
                    is making up a round -- so it keeps the live link. The
                    scoreboard recomputes from the card either way, and a week
                    whose result can still change is exactly the one worth
                    watching. */}
                {(event.state === 'in_progress' ||
                  event.state === 'paused' ||
                  event.state === 'provisional') && (
                  <Link to={`/events/${event.id}`} className="schedule-link live-link">
                    Live
                  </Link>
                )}
                {/* Below the badge and its link, on its own line: it is a
                    sentence rather than a tag, and the Status column is the
                    narrowest thing on the row holding one. */}
                <AwaitingNote event={event} />
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  )
}

/**
 * What a night produced: the scoring result and every match, one to a line.
 *
 * # Three columns became one
 *
 * Gross and Net were columns of their own, which worked while a row named a
 * single winner. Adding the match results made them mostly empty -- a match is
 * won on HOLES, so its winner's totals say nothing about it -- and left two
 * numeric columns carrying one line between them. Commissioner, 2026-09-29:
 * *"I wonder if it makes sense to have the net and gross columns and for the
 * low net in the winner col just add a `41 (-5)`."*
 *
 * So the figure goes beside the name that earned it, and the table loses two of
 * its ten columns on a page that has never fitted a phone without scrolling.
 *
 * # The NET, because that is what was won
 *
 * The label says `low net`, and low net is not low gross -- this league's own
 * fixture has Shawn four strokes better on gross and Mike taking the net. One
 * figure has to be the right one. The gross is on the event's own page, a click
 * away, next to the card it came from.
 */
function WinnerCells({ winners }: { winners: Winner[] }) {
  const { pathFor } = usePlayerPaths()
  if (winners.length === 0) {
    return <td />
  }
  const tied = winners.length > 1 && winners.every((w) => w.label === 'winner')
  const lines = (render: (w: Winner) => React.ReactNode) =>
    winners.map((w) => (
      <div key={`${w.player_id}-${w.label}`} className="winner-line">
        {render(w)}
      </div>
    ))
  return (
    <td data-testid="event-winners">
      {lines((w) => (
        <>
          <Link to={pathFor(w.player_id)} className="winner-name">
            {w.display_name}
          </Link>
          {tied && <span className="note"> (tied)</span>}
          {/* A match names the pair and the margin; anything else names what
              was won and what it was won with. "Steve beat Dad 2&1" and
              "Shawn low net 36 (even)" are the two things a row can say. */}
          {w.opponent_display_name ? (
            <span className="note">
              {' '}
              beat {w.opponent_display_name}
              {howWon(w)}
            </span>
          ) : (
            <>
              {w.label !== 'winner' && <span className="note"> {winnerLabel[w.label]}</span>}
              {w.net !== null && (
                <span className="winner-scores"> {withToPar(w.net, w.net_to_par)}</span>
              )}
            </>
          )}
        </>
      ))}
    </td>
  )
}

/**
 * One event in a list.
 *
 * # The stroke sheet is linked from a scheduled event, not from a played one
 *
 * It is a PRE-round sheet: which holes you receive strokes on, consulted before
 * play (FR-024e). Once the night is done, the same figures are on the event
 * itself as what actually governed it (FR-027b), so pointing at the sheet there
 * would offer a projection of something already settled.
 */
export function EventLine({ event }: { event: EventSummary }) {
  return (
    <li className={`event-line event-${event.state}`}>
      <div className="event-line-main">
        <Link to={`/events/${event.id}`} className="event-line-title">
          Week {event.week_number} · {event.course}
        </Link>
        <span className={`badge badge-${event.state}`}>{STATE_LABEL[event.state]}</span>
      </div>
      <div className="event-line-meta">
        <time dateTime={event.scheduled_date}>{event.scheduled_date}</time>
        <span>
          {event.tee} tee · {event.holes_expected} holes · {event.event_type.display_name}
        </span>
        {event.state === 'scheduled' && (
          <Link to={`/events/${event.id}/stroke-sheet`}>Stroke sheet</Link>
        )}
        {/* An event being played is the one thing on this page somebody wants
            to open RIGHT NOW, so it links straight to the live views rather
            than by way of the scorecard (FR-035a, FR-053).
            **Provisional included**, as in the table: somebody is making up a
            round, and a week whose result can still change is the one worth
            watching. The two layouts are one page at two widths and had
            disagreed about this since the state was added. */}
        {(event.state === 'in_progress' ||
          event.state === 'paused' ||
          event.state === 'provisional') && (
          <>
            <Link to={`/events/${event.id}`} className="live-link">
              Live
            </Link>
            <Link to={`/events/${event.id}/tracker`}>Round tracker</Link>
          </>
        )}
      </div>
      {event.winners.length > 0 && <Winners winners={event.winners} />}
      <AwaitingNote event={event} />
    </li>
  )
}

const winnerLabel: Record<Winner['label'], string> = {
  winner: 'Winner',
  low_net: 'Low net',
  match_won: 'Won',
  match_forfeit: 'Won',
}

/**
 * Who a scored week is still waiting for (FR-071b).
 *
 * # The badge cannot say this, and that is why this exists
 *
 * `Scored — not final` is deliberately vague: it used to read "awaiting a
 * round", which became false once a week with every card in could still sit
 * provisional (FR-066a), and a label that is sometimes false is worse than a
 * vaguer one that is always true. Naming the people is always true. Commissioner,
 * 2026-10-05: *"we should make it more obviously who is yet to play once a week
 * is scored not final."*
 *
 * # The hole count is the point, not decoration
 *
 * "Tom — 0 of 9" is a man who has not played; "Tom — 4 of 9" is a card halfway
 * in. They read completely differently to anybody deciding whether to wait, and
 * the badge collapses both into "not final".
 */
function AwaitingNote({ event }: { event: EventSummary }) {
  const { pathFor } = usePlayerPaths()
  const awaiting = event.awaiting ?? []
  if (awaiting.length === 0) return null
  return (
    <span className="schedule-awaiting" data-testid={`awaiting-${event.id}`}>
      Waiting on{' '}
      {awaiting.map((a, i) => (
        <span key={a.player_id}>
          {i > 0 && ', '}
          <Link to={pathFor(a.player_id)}>{a.display_name}</Link>
          {a.holes > 0 && ` (${a.holes} of ${a.holes_expected})`}
        </span>
      ))}
    </span>
  )
}

/** A match result, as opposed to the night's scoring result. */
function isMatch(w: Winner): boolean {
  return w.label === 'match_won' || w.label === 'match_forfeit'
}

/** How a match was won, after the opponent's name. A forfeit has no margin. */
function howWon(w: Winner): string {
  if (w.label === 'match_forfeit') return ' by forfeit'
  return w.margin ? ` ${w.margin}` : ''
}

/**
 * Who won a completed night, with gross and net against par — "44 (+8)".
 *
 * The scores are shown as they arrive; nothing here adds or compares them
 * (Constitution III). A night recorded without a card has a winner and no
 * scores, and says so by leaving them out rather than showing zeros.
 */
function Winners({ winners }: { winners: Winner[] }) {
  const { pathFor } = usePlayerPaths()
  const shared = winners.length > 1 && winners.every((w) => w.label === 'winner')
  return (
    <ul className="event-line-winners" data-testid="event-winners">
      {winners.map((w) => (
        <li key={`${w.player_id}-${w.label}`}>
          <span className="winner-label">{shared ? 'Tied winner' : winnerLabel[w.label]}</span>{' '}
          <Link to={pathFor(w.player_id)} className="winner-name">
            {w.display_name}
          </Link>
          {w.opponent_display_name && (
            <span className="winner-scores">
              {' '}
              beat {w.opponent_display_name}
              {howWon(w)}
            </span>
          )}
          {/* The NET, matching the table. This showed gross AND net, which was
              fine while a row named one winner -- but the two layouts are the
              same page at different widths, and showing different figures in
              each is a worse confusion than a phone card being a little
              sparser. The gross is on the event's own page. */}
          {!isMatch(w) && w.net !== null && (
            <span className="winner-scores"> {withToPar(w.net, w.net_to_par)}</span>
          )}
        </li>
      ))}
    </ul>
  )
}
